// src/features/statistics/activity-charts.tsx
import { Kicker } from "@/components/iris/kicker";
import { formatCount } from "@/lib/format-count";
import type { DailyCount } from "@/lib/types";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CollapsibleCard } from "./chart-primitives";

interface ActivityChartsProps {
  entities: DailyCount[];
  messages: DailyCount[];
  groups: DailyCount[];
}

/**
 * SVG-safe hex colors for line chart series.
 * CSS vars don't work reliably in SVG stroke attributes.
 */
const LINE_COLORS = {
  entities: "#ff2ea6", // --iris-neon-magenta // token-exempt
  messages: "#a855f7", // --iris-neon-purple // token-exempt
  groups: "#39ff88", // --iris-neon-green // token-exempt
} as const;

/** 7-day moving-average overlay stroke — gold dashed, IRIS "Activity Over Time" (IR-1046 ④) */
const AVG_COLOR = "#ff9933"; // --iris-neon-orange // token-exempt

/** Gridline stroke — design spec: rgba(255,255,255,0.04) */
const GRID_STROKE = "rgba(255,255,255,0.04)";

/** Axis tick fill — --iris-fg-4 */
const AXIS_TICK_FILL = "#6b7184"; // --iris-fg-4 // token-exempt

const TOOLTIP_STYLE = {
  background: "var(--iris-bg-3)",
  border: "1px solid var(--iris-line-2)",
  borderRadius: "var(--iris-r-sm)",
  fontSize: "var(--iris-t-small)",
  color: "var(--iris-fg-2)",
} as const;

interface DailyAvgPoint extends DailyCount {
  /** Trailing N-day moving average of `count`; null only for an empty series. */
  avg: number | null;
}

/**
 * Attach a trailing N-day moving average to each daily point. The window grows
 * until it fills (point i averages points [i-N+1 .. i], clamped at the start),
 * so early days aren't dropped. Pure + side-effect-free for unit testing — no
 * dependency on the chart. Missing/non-numeric counts are treated as zero.
 */
export function withMovingAverage(data: DailyCount[], window = 7): DailyAvgPoint[] {
  if (!Array.isArray(data)) return [];
  return data.map((d, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = data.slice(start, i + 1);
    const sum = slice.reduce((s, p) => s + (Number(p.count) || 0), 0);
    return { ...d, avg: slice.length > 0 ? sum / slice.length : null };
  });
}

function LegendSwatch({
  color,
  label,
  dashed,
}: { color: string; label: string; dashed?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 16,
          height: 0,
          borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`,
        }}
      />
      <span className="iris-meta">{label}</span>
    </span>
  );
}

function ActivityLineChart({
  title,
  data,
  color,
}: { title: string; data: DailyCount[]; color: string }) {
  const series = withMovingAverage(data);
  return (
    <CollapsibleCard title={title}>
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <LegendSwatch color={color} label="Daily" />
        <LegendSwatch color={AVG_COLOR} label="7-day avg" dashed />
      </div>
      <div style={{ height: 192 }}>
        {data.length === 0 ? (
          <div
            style={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--iris-fg-5)",
              fontSize: "var(--iris-t-small)",
            }}
          >
            No data in the last 30 days
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: AXIS_TICK_FILL }}
                tickFormatter={(v: string) => {
                  const d = new Date(v);
                  return `${String(d.getMonth() + 1)}/${String(d.getDate())}`;
                }}
              />
              {/* IR-1046 ⑤: compact axis ticks "as done in IRIS". */}
              <YAxis
                tick={{ fontSize: 10, fill: AXIS_TICK_FILL }}
                tickFormatter={(v: number) => formatCount(v)}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelFormatter={(label) => new Date(String(label)).toLocaleDateString()}
                // Round the 7-day-avg float to a whole number; show exact counts.
                formatter={(value, name) => [Math.round(Number(value) || 0).toLocaleString(), name]}
              />
              <Line
                type="monotone"
                dataKey="count"
                name="Daily"
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="avg"
                name="7-day avg"
                stroke={AVG_COLOR}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                activeDot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </CollapsibleCard>
  );
}

export function ActivityCharts({ entities, messages, groups }: ActivityChartsProps) {
  return (
    <>
      <Kicker style={{ marginTop: 32, marginBottom: 12 }}>30-Day Activity</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        <ActivityLineChart
          title="New Entities per Day"
          data={entities}
          color={LINE_COLORS.entities}
        />
        <ActivityLineChart
          title="New Messages per Day"
          data={messages}
          color={LINE_COLORS.messages}
        />
        <ActivityLineChart title="New Groups per Day" data={groups} color={LINE_COLORS.groups} />
      </div>
    </>
  );
}
